import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { createContext } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'
import { setBodyContextValue } from 'components/shared/context/body-context.js'
import { style } from './login-slide-in.style.js'
import './login.js'

export const PageLoginSlideInContext = createContext<CSCLoginSlideIn | null>(Symbol('csc-page-login-slide-in-context'))

const pageLoginSlideIns: CSCLoginSlideIn[] = []

const publishPageLoginSlideIn = () => setBodyContextValue(PageLoginSlideInContext, pageLoginSlideIns[0] ?? null)

@customElement('csc-login-slide-in')
export class CSCLoginSlideIn extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  /** Passed through to `csc-login` for the API as `return_url`. */
  @property({ type: String, attribute: 'return-url' })
  returnUrl = ''

  /** Passed through to `csc-login` for the forgot-password link. */
  @property({ type: String, attribute: 'forgot-password-url' })
  forgotPasswordUrl = ''

  /** Passed through to `csc-login` as its `variant`. */
  @property({ type: String, attribute: 'login-variant' })
  loginVariant: 'email' | 'mfa' = 'mfa'

  @state()
  private hasDetails = false

  private urls = new BaseUrlController(this)

  @query('cse-slide-in')
  private slideIn?: CSESlideIn

  private opening: Promise<void> = Promise.resolve()

  override connectedCallback() {
    super.connectedCallback()
    if (this.getRootNode() !== document) return
    pageLoginSlideIns.push(this)
    publishPageLoginSlideIn()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    const index = pageLoginSlideIns.indexOf(this)
    if (index === -1) return
    pageLoginSlideIns.splice(index, 1)
    publishPageLoginSlideIn()
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete()
    await this.opening
    return result
  }

  open = async (): Promise<void> => {
    this.opening = this.openSlideIn()
    await this.opening
  }

  private async openSlideIn(): Promise<void> {
    if (!this.hasUpdated) await super.getUpdateComplete()

    const slideIn = this.slideIn
    if (!slideIn) return

    await slideIn.updateComplete
    await slideIn.open()
  }

  private onDetailsSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement
    this.hasDetails = slot.assignedNodes({ flatten: true }).length > 0
  }

  override render() {
    return html`
      <cse-slide-in>
        <div slot="header">
          <slot name="header">
            ${t('header', { ns: 'csc-login-slide-in', default: 'Login' })}
          </slot>
        </div>

        <div class="body">
          <csc-login
            variant="${this.loginVariant}"
            api-base-url="${this.urls.href('')}"
            return-url="${this.returnUrl}"
            forgot-password-url="${this.forgotPasswordUrl}"
          >
            <slot name="heading" slot="heading"></slot>
            <slot name="description" slot="description"></slot>
          </csc-login>

          <div class="details" ?hidden="${!this.hasDetails}">
            <cse-divider orientation="horizontal" variant="solid"></cse-divider>
            <slot name="details" @slotchange="${this.onDetailsSlotChange}"></slot>
          </div>
        </div>
      </cse-slide-in>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-login-slide-in': CSCLoginSlideIn
  }
}
